import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, LineElement, PointElement, Filler } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { SimulationResultsProps } from '../types/simulation';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  Filler
);

const SimulationResults: React.FC<SimulationResultsProps> = ({ results, monthlyInvestment, years, initialInvestment = 0 }) => {
  const { final_values, summary, monthly_paths, monthly_percentiles, capital_line_monthly } = results;
  const months = years * 12;

  // 数値フォーマット関数
  const formatCurrency = (value: number): string => {
    const man = Math.round(value / 10000);
    return new Intl.NumberFormat('ja-JP').format(man);
  };

  const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  // ヒストグラムデータ作成
  const createHistogramData = () => {
    // 総ビン数（表示の粒度）
    const totalBins = 40;
    const capitalTotal = summary.total_investment;
    const total = final_values.length;

    // 万単位表示
    const formatMan = (value: number): string => {
      const manCeil = Math.ceil(value / 10000);
      if (manCeil <= 1) return '≤1万';
      return `${new Intl.NumberFormat('ja-JP').format(manCeil)}万`;
    };
    const formatManRange = (lower: number, upper: number): string => {
      const lowerMan = lower <= 0 ? 0 : Math.ceil(lower / 10000);
      const upperMan = Math.max(lowerMan + 1, Math.ceil(upper / 10000));
      if (lowerMan === 0 && upperMan === 1) return '0〜1万';
      const nf = new Intl.NumberFormat('ja-JP');
      return `${nf.format(lowerMan)}〜${nf.format(upperMan)}万`;
    };

    // 3段階ビニング: A) ≤0, B) 0 < v ≤ 0.1×元本, C) >0.1×元本（ログ分割）
    const binAValues = final_values.filter(v => v <= 0);
    const cap10 = capitalTotal * 0.10;
    const binBValues = final_values.filter(v => v > 0 && v <= cap10);
    const restValuesSorted = final_values
      .filter(v => v > cap10)
      .sort((a, b) => a - b);

    // 残りビン数（A/Bが空の場合はその分増える）
    const binsForRest = Math.max(0, totalBins - (binAValues.length > 0 ? 1 : 0) - (binBValues.length > 0 ? 1 : 0));

    const labels: string[] = [];
    const probsRaw: number[] = [];
    const backgroundColors: string[] = [];
    const borderColors: string[] = [];

    // A: ≤0
    if (binAValues.length > 0) {
      labels.push('≤0万');
      probsRaw.push((binAValues.length / total) * 100);
      backgroundColors.push('rgba(153, 27, 27, 0.75)');
      borderColors.push('rgba(153, 27, 27, 1)');
    }

    // B: ≤10%元本
    if (binBValues.length > 0) {
      labels.push(`≤${formatMan(cap10)}`);
      probsRaw.push((binBValues.length / total) * 100);
      backgroundColors.push('rgba(220, 38, 38, 0.6)');
      borderColors.push('rgba(220, 38, 38, 1)');
    }

    // C+: >10%元本をログ等間隔で binsForRest に分割
    if (binsForRest > 0 && restValuesSorted.length > 0) {
      const restMin = cap10; // 下端はcap10
      const restMax = restValuesSorted[restValuesSorted.length - 1];
      const logMin = Math.log10(Math.max(restMin, 1));
      const logMax = Math.log10(restMax);
      const step = binsForRest > 0 ? (logMax - logMin) / binsForRest : 0;

      // 累積ポインタで差分カウント
      let prevEdge = restMin;
      let ptr = 0;
      for (let i = 1; i <= binsForRest; i++) {
        const upper = i === binsForRest ? restMax : Math.pow(10, logMin + i * step);
        while (ptr < restValuesSorted.length && restValuesSorted[ptr] <= upper) ptr++;
        // このビンの件数 = 累積件数 - これまでのビンの件数（A/B含む）
        const countedBefore = Math.round(probsRaw.reduce((a, b) => a + b, 0) * total / 100);
        const binCount = Math.max(0, ptr + binAValues.length + binBValues.length - countedBefore);
        const pct = (binCount / total) * 100;

        labels.push(formatManRange(prevEdge, upper));
        probsRaw.push(pct);
        const isBelowOrEqual = upper <= capitalTotal;
        if (isBelowOrEqual) {
          backgroundColors.push('rgba(220, 38, 38, 0.6)');
          borderColors.push('rgba(220, 38, 38, 1)');
        } else {
          backgroundColors.push('rgba(59, 130, 246, 0.6)');
          borderColors.push('rgba(59, 130, 246, 1)');
        }
        prevEdge = upper;
      }
    }

    const probsRounded = probsRaw.map(v => parseFloat(v.toFixed(2)));
    return {
      labels,
      datasets: [
        {
          label: '確率（%）',
          data: probsRounded,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          rawProbs: probsRaw as any,
        } as any,
      ],
    };
  };

  // 資産推移データ作成（塗り分けエリア）
  const createPathData = () => {
    const labels = Array.from({ length: months + 1 }, (_, i) => i);
    
    // monthly_percentiles があればそれを使用。なければ monthly_paths から計算
    const monthlyStats = monthly_percentiles && monthly_percentiles.length === months + 1
      ? monthly_percentiles
      : (() => {
          const stats = [] as any[];
          for (let month = 0; month <= months; month++) {
            const monthlyValues = monthly_paths.map(path => path[month]);
            monthlyValues.sort((a, b) => a - b);
            const trials = monthlyValues.length;
            stats.push({
              percentile_10: monthlyValues[Math.floor(trials * 0.10)],
              percentile_25: monthlyValues[Math.floor(trials * 0.25)],
              percentile_40: monthlyValues[Math.floor(trials * 0.40)],
              median: monthlyValues[Math.floor(trials * 0.50)],
              percentile_60: monthlyValues[Math.floor(trials * 0.60)],
              percentile_75: monthlyValues[Math.floor(trials * 0.75)],
              percentile_90: monthlyValues[Math.floor(trials * 0.90)],
            });
          }
          return stats;
        })();
    
    // データセットを作成（外側→内側の順で重ねる）
    const datasets = [
      // 広範囲（10-90%）
      {
        label: 'p10',
        data: monthlyStats.map(s => s.percentile_10),
        borderColor: 'rgba(59, 130, 246, 0)',
        backgroundColor: 'rgba(0,0,0,0)',
        borderWidth: 0,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        order: 1,
      },
      {
        label: '90%区間 (10–90%)',
        data: monthlyStats.map(s => s.percentile_90),
        borderColor: 'rgba(37, 99, 235, 0.6)',
        backgroundColor: 'rgba(59, 130, 246, 0.18)',
        borderWidth: 1,
        fill: { target: '-1' },
        tension: 0.1,
        pointRadius: 0,
        order: 2,
      },

      // 主要帯（25-75%）
      {
        label: 'p25',
        data: monthlyStats.map(s => s.percentile_25),
        borderColor: 'rgba(37, 99, 235, 0.3)',
        backgroundColor: 'rgba(0,0,0,0)',
        borderWidth: 1,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        order: 3,
      },
      {
        label: '50%区間 (25–75%)',
        data: monthlyStats.map(s => s.percentile_75),
        borderColor: 'rgba(37, 99, 235, 0.8)',
        backgroundColor: 'rgba(59, 130, 246, 0.33)',
        borderWidth: 1,
        fill: { target: '-1' },
        tension: 0.1,
        pointRadius: 0,
        order: 4,
      },

      // 中心帯（40-60%）
      {
        label: 'p40',
        data: monthlyStats.map(s => s.percentile_40),
        borderColor: 'rgba(37, 99, 235, 0.4)',
        backgroundColor: 'rgba(0,0,0,0)',
        borderWidth: 1,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        order: 5,
      },
      {
        label: '20%区間 (40–60%)',
        data: monthlyStats.map(s => s.percentile_60),
        borderColor: 'rgba(37, 99, 235, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.50)',
        borderWidth: 1,
        fill: { target: '-1' },
        tension: 0.1,
        pointRadius: 0,
        order: 6,
      },

      // 中央値（太線）
      {
        label: '中央値',
        data: monthlyStats.map(s => s.median),
        borderColor: 'rgba(37, 99, 235, 1)',
        backgroundColor: 'rgba(37, 99, 235, 0.08)',
        borderWidth: 3,
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 6,
        order: 10,
      },
    ];
    
    return {
      labels,
      datasets
    };
  };

  // 元本ライン（資産推移用、円ベース）
  const capitalLineYen = capital_line_monthly && capital_line_monthly.length === months + 1
    ? capital_line_monthly
    : Array.from({ length: months + 1 }, (_, i) => (initialInvestment * 10000) + (monthlyInvestment * 10000 * i));

  const [histHoverIndex, setHistHoverIndex] = React.useState<number | null>(null);
  const histogramData = (() => {
    const base = createHistogramData();
    // 既存の色分類を元に、ホバー以下を強調（不透明度アップ）
    const newBackground = (base.datasets[0] as any).backgroundColor.map((c: string, idx: number) => {
      if (histHoverIndex == null) return c;
      const isActive = idx <= histHoverIndex;
      if (!isActive) return c;
      // 強調：不透明度を0.9に
      return c.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[^)]+\)/, 'rgba($1,$2,$3,0.9)');
    });
    const newBorder = (base.datasets[0] as any).borderColor.map((c: string, idx: number) => {
      if (histHoverIndex == null) return c;
      return idx <= histHoverIndex ? c : c;
    });
    return {
      ...base,
      datasets: [
        {
          ...(base.datasets[0] as any),
          backgroundColor: newBackground,
          borderColor: newBorder,
        }
      ]
    };
  })();
  const pathData = (() => {
    const base = createPathData();
    // 既存データに元本線を追加（円ベース）
    const capitalDataset = {
      label: '元本（累計）',
      data: capitalLineYen, // createPathDataは円ベース前提
      borderColor: 'rgba(220,38,38,0.9)',
      borderWidth: 2,
      fill: false,
      tension: 0,
      pointRadius: 0,
      order: 9,
    } as any;
    return { ...base, datasets: [...base.datasets, capitalDataset] };
  })();

  const histogramOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: function(items: any[]) {
            return items && items[0] ? items[0].label : '';
          },
          label: function(ctx: any) {
            const val = typeof ctx.raw === 'number' ? ctx.raw : Number(ctx.raw);
            return `このビン: ${val.toFixed(2)}%`;
          },
          afterLabel: function(ctx: any) {
            try {
              const raw = (ctx.dataset as any).rawProbs as number[];
              const idx = ctx.dataIndex as number;
              const sum = raw.slice(0, idx + 1).reduce((a, b) => a + b, 0);
              const capped = Math.min(100, sum);
              return `累計: ${capped.toFixed(2)}%`;
            } catch (_) {
              return '';
            }
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: false },
        ticks: {
          callback: function(value: any) {
            return `${Number(value).toFixed(0)}%`;
          }
        }
      },
      x: {
        title: { display: false }
      }
    },
    onHover: (_: any, elements: any[]) => {
      if (elements && elements.length > 0) setHistHoverIndex(elements[0].index);
      else setHistHoverIndex(null);
    }
  };

  // ヒストグラムのホバー位置に縦ラインを引くプラグイン
  const histHoverLinePlugin = {
    id: 'histHoverLine',
    afterDatasetsDraw: (chart: any) => {
      if (histHoverIndex == null) return;
      const { ctx, scales, data } = chart;
      const xScale = scales.x;
      const yScale = scales.y;
      if (!xScale || !yScale) return;
      const label = data.labels?.[histHoverIndex];
      const x = xScale.getPixelForValue(label ?? histHoverIndex);
      ctx.save();
      ctx.strokeStyle = 'rgba(107,114,128,0.9)'; // gray-500
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(x, yScale.top);
      ctx.lineTo(x, yScale.bottom);
      ctx.stroke();
      ctx.restore();
    }
  } as any;

  const pathOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          filter: function(item: any) {
            const keep = ['中央値', '元本（累計）'];
            return keep.includes(item.text);
          },
        },
      },
      title: { display: false },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        filter: function(tooltipItem: any) {
          const label = tooltipItem.dataset.label as string;
          const keep = ['90%区間 (10–90%)', '50%区間 (25–75%)', '20%区間 (40–60%)', '中央値', 'p40', 'p25', 'p10', '元本（累計）'];
          return keep.includes(label);
        },
        itemSort: (a: any, b: any) => {
          const order: Record<string, number> = {
            '90%区間 (10–90%)': 1,
            '50%区間 (25–75%)': 2,
            '20%区間 (40–60%)': 3,
            '中央値': 4,
            'p40': 5,
            'p25': 6,
            'p10': 7,
            '元本（累計）': 8,
          };
          return order[(a.dataset?.label as string) || ''] - order[(b.dataset?.label as string) || ''];
        },
        callbacks: {
          title: function(context: any) {
            const month = Number(context[0].label);
            const year = Math.round(month / 12);
            return `投資${year}年後`;
          },
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label === 'p40') label = '40%';
            if (label === 'p25') label = '25%';
            if (label === 'p10') label = '10%';
            if (label === '90%区間 (10–90%)') label = '90%';
            if (label === '50%区間 (25–75%)') label = '75%';
            if (label === '20%区間 (40–60%)') label = '60%';
            const value = context.parsed.y;
            return `${label}: ${formatCurrency(value)}万円`;
          }
        }
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: '資産額（万円）',
        },
        ticks: {
          callback: function(value: any) {
            return new Intl.NumberFormat('ja-JP').format(Math.round(Number(value) / 10000)) + '万円';
          }
        }
      },
      x: {
        title: {
          display: true,
          text: '投資年数',
        },
        ticks: {
          maxTicksLimit: 8,
          callback: function(value: any) {
            const month = Number(value);
            const year = Math.round(month / 12);
            
            // 月数を年数に変換して表示
            if (month % 12 === 0) {
              return `${year}年`;
            }
            return '';
          }
        }
      }
    }
  };

  const returnOnInvestment = ((summary.mean - summary.total_investment) / summary.total_investment) * 100;

  // 右端に各値を常時表示するプラグイン
  const rightLabelsPlugin = {
    id: 'rightLabels',
    afterDatasetsDraw: (chart: any) => {
      const { ctx, data, scales } = chart;
      const xScale = scales.x;
      const yScale = scales.y;
      if (!xScale || !yScale) return;
      const lastIndex = data.labels.length - 1;
      const showLabels = new Set(['中央値', '元本（累計）']);
      data.datasets.forEach((ds: any) => {
        if (!showLabels.has(ds.label)) return;
        const yVal = ds.data[lastIndex];
        if (yVal == null) return;
        const x = xScale.getPixelForValue(lastIndex);
        const y = yScale.getPixelForValue(yVal);
        ctx.save();
        ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto';
        ctx.fillStyle = 'rgba(55,65,81,1)';
        let label = ds.label;
        const text = `${label}: ${formatCurrency(yVal)}万円`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, x + 8, y);
        ctx.restore();
      });
    }
  } as any;

  return (
    <div className="space-y-6">
      {/* サマリー統計（横方向に間延びしないグリッド） */}
      <div className="card">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">📈 シミュレーション結果</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4 mb-6">
          <div className="stat-card">
            <h3 className="text-sm font-medium text-gray-600">投資元本</h3>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(summary.total_investment)}万円
            </p>
          </div>
          
          <div className="stat-card">
            <h3 className="text-sm font-medium text-gray-600">平均最終金額</h3>
            <p className="text-2xl font-bold text-success-600">
              {formatCurrency(summary.mean)}万円
            </p>
          </div>
          
          <div className="stat-card">
            <h3 className="text-sm font-medium text-gray-600">中央値</h3>
            <p className="text-2xl font-bold text-primary-600">
              {formatCurrency(summary.median)}万円
            </p>
          </div>
          
          <div className="stat-card">
            <h3 className="text-sm font-medium text-gray-600">期待収益率</h3>
            <p className={`text-2xl font-bold ${returnOnInvestment >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
              +{returnOnInvestment.toFixed(1)}%
            </p>
          </div>
        </div>

        {/* リスク指標 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="stat-card">
            <h3 className="text-sm font-medium text-gray-600">下位10%（悲観シナリオ）</h3>
            <p className="text-xl font-bold text-warning-600">
              {formatCurrency(summary.percentile_10)}万円
            </p>
          </div>
          
          <div className="stat-card">
            <h3 className="text-sm font-medium text-gray-600">上位10%（楽観シナリオ）</h3>
            <p className="text-xl font-bold text-success-600">
              {formatCurrency(summary.percentile_90)}万円
            </p>
          </div>

          <div className="stat-card">
            <h3 className="text-sm font-medium text-gray-600">元本割れ確率</h3>
            <p className="text-xl font-bold text-danger-600">
              {formatPercentage(summary.probability_capital_loss)}
            </p>
          </div>
        </div>
      </div>

      {/* グラフ2カラム（横方向レイアウト） */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* ヒストグラム */}
        <div className="card">
          <h3 className="text-xl font-bold text-gray-800 mb-4">📊 最終金額の分布</h3>
          <div className="h-80 sm:h-72">
            <Bar data={histogramData} options={histogramOptions} plugins={[histHoverLinePlugin]} />
          </div>
          {/* 累計はツールチップに移動 */}
        </div>

        {/* 資産推移例 */}
        <div className="card">
          <h3 className="text-xl font-bold text-gray-800 mb-4">📈 資産推移の確率分布</h3>
          <div className="h-80 sm:h-72">
            <Line data={pathData} options={pathOptions} plugins={[rightLabelsPlugin]} />
          </div>
          <p className="text-sm text-gray-600 mt-2">
            横軸：投資年数、縦軸：資産額。色が濃いほどその資産額に到達する確率が高い範囲です。表示は<strong>20%区間 (40–60%)</strong>、<strong>50%区間 (25–75%)</strong>、<strong>90%区間 (10–90%)</strong>と<strong>中央値</strong>。
          </p>
          <p className="text-xs text-gray-500 mt-1">凡例は上部をご覧ください（20%区間／50%区間／90%区間／中央値）。</p>
        </div>
      </div>

      {/* 解釈と注意点 */}
      <div className="card bg-amber-50 border-amber-200">
        <h3 className="text-lg font-bold text-amber-800 mb-3">⚠️ 結果の解釈について</h3>
        <div className="space-y-2 text-sm text-amber-700">
          <p><strong>平均値：</strong> 多数回繰り返した場合の期待値です。</p>
          <p><strong>中央値：</strong> 50%の確率でこの金額以上になります。</p>
          <p><strong>下位10%：</strong> 10%の確率でこの金額を下回ります（リスクシナリオ）。</p>
          <p><strong>上位10%：</strong> 10%の確率でこの金額を上回ります（ベストシナリオ）。</p>
          <p className="pt-2 font-medium">
            ※ これらはシミュレーション結果であり、実際の投資成果を保証するものではありません。
          </p>
        </div>
      </div>
    </div>
  );
};

export default SimulationResults;